import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import type { IntelligenceTopicRecord } from "@graftus/schema";
import type { PulseCompletionWithFrequency } from "@/hooks/use-pulse-check-in-nudge";
import type { CurrentTopicKey, TopicKey } from "./topics";
import { cn } from "@/lib/utils";
import { PulseCheckInChatNudge } from "./PulseCheckInChatNudge";
import { PulseSetupChatNudge } from "./PulseSetupChatNudge";
import { TOPIC_ICONS } from "./topic-icons";

interface Props {
  contextLabel: string;
  topicKey: TopicKey;
  onTopicChange: (key: CurrentTopicKey) => void;
  topics: IntelligenceTopicRecord[];
  /** Fills the composer; user sends explicitly (Enter / Skicka). */
  onPrefillPrompt: (prompt: string) => void;
  pulseCheckInSummary?: PulseCompletionWithFrequency | null;
  showPulseSetupNudge?: boolean;
  /** Drop the icon + heading block; the landing greeting already says it. */
  hideHeading?: boolean;
}

export function ChatStarters({
  contextLabel,
  topicKey,
  onTopicChange,
  topics,
  onPrefillPrompt,
  pulseCheckInSummary,
  showPulseSetupNudge = false,
  hideHeading = false,
}: Props) {
  const { t } = useTranslation("intelligence");

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-2xl flex-col items-center justify-center gap-6 px-4 text-center",
        hideHeading ? "py-0" : "h-full py-10",
      )}
    >
      {showPulseSetupNudge ? (
        <PulseSetupChatNudge />
      ) : pulseCheckInSummary ? (
        <PulseCheckInChatNudge
          summary={pulseCheckInSummary}
          onPrefillPrompt={onPrefillPrompt}
        />
      ) : null}
      {!hideHeading && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className="font-display text-xl font-normal">
            {t("chat.starters.heading", { context: contextLabel })}
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {t("chat.composer.placeholder")}
          </p>
        </div>
      )}

      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
        {topics.map((t) => {
          const Icon = TOPIC_ICONS[t.key] ?? Sparkles;
          const active = t.key === topicKey;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                onTopicChange(t.key);
                onPrefillPrompt(t.starter);
              }}
              className={cn(
                "group flex items-start gap-3 rounded-xl border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent/40",
                active && "border-primary/50 bg-accent/30",
              )}
            >
              <div
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors",
                  "group-hover:bg-primary/10 group-hover:text-primary",
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium">{t.title}</div>
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                  {t.starter}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
